import { ButtonGroup, LargeIconButton } from '@recruit-robin/robin-theme/web-components';
import {
  FileSearch_02,
  FileSearch_02Fill,
  FolderSearch,
  FolderSearchFill,
  LayoutLeft,
  LayoutLeftFill,
  PieChart_01,
  PieChart_01Fill,
  Settings_02,
} from '@recruit-robin/robin-theme/web-icons';
import { theme } from '@recruit-robin/robin-theme/web-styles';
import { useContext } from 'react';

import { ChatOverviewList } from '@/features/chat-overview';
import { useIsRobinJrApp } from '@/shared/config/useRobinJrApp';
import { Typography } from 'components/ui/atoms/Typography';
import { routes } from 'config/routes';
import { AuthContext } from 'shared/legacy/contexts/AuthContext';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SidebarOverlayFixedContext } from 'shared/legacy/contexts/SidebarOverlayFixedContext';
import { useAuth } from 'shared/legacy/hooks/useAuth';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useContainer } from './Sidebar.useContainer';
import { useStyles } from './SidebarNav.styles';
import { SidebarNavPages } from './SidebarNav.types';
import { useSelectedRoute } from './SidebarNav.useSelectedRoute';
import { SidebarNavSettingsMenu } from './SidebarNavSettingsMenu';
import { SidebarOverlayFloating } from './SidebarOverlayFloating';

const SIDEBAR_MENU_ICONS_SIZE = 20;

const SidebarNav = () => {
  const { isStaff } = useAuth();
  const classes = useStyles();
  const { navigateTo, verifyAndNavigateToSearchRoute } = useContainer();
  const { dictionary } = useContext(LocalizationContext);
  const { isOpen: isFixedOpen, setIsOpen: setIsFixedOpen } = useContext(SidebarOverlayFixedContext);
  const activeRoute = useSelectedRoute();
  const { activeTokenType, disposeImpersonatedToken } = useContext(AuthContext);
  const candidateOutreachModal = useGlobalDialog(GlobalDialogType.CandidateOutreach);
  const isRobinJrApp = useIsRobinJrApp();

  const closeOutreachModalOnClick = (callback: () => void) => {
    candidateOutreachModal.close();
    callback();
  };

  return (
    <ButtonGroup gap={16}>
      <LargeIconButton
        selected={SidebarNavPages.Campaign === activeRoute}
        icon={(selected) => {
          return selected ? (
            <FileSearch_02Fill size={SIDEBAR_MENU_ICONS_SIZE} />
          ) : (
            <FileSearch_02 size={SIDEBAR_MENU_ICONS_SIZE} />
          );
        }}
        onClick={() =>
          closeOutreachModalOnClick(() => {
            if (!isRobinJrApp) {
              verifyAndNavigateToSearchRoute();
            }

            setIsFixedOpen(!isFixedOpen);
          })
        }
        tooltip={!isFixedOpen && (isRobinJrApp ? <ChatOverviewList /> : <SidebarOverlayFloating />)}
        tooltipStyle={classes.fileSearchIcon}
        tooltipArrowStyle={classes.fileSearchIconArrow}
        tooltipArrowStrokeStyle={theme.colors.neutral.variant[10]}
        tooltipArrowStrokeWidthStyle={1}
        customTooltipPosition={{ top: 64, left: 0 }}
        tooltipArrow
      />
      <LargeIconButton
        selected={SidebarNavPages.RatedCandidates === activeRoute}
        icon={(selected) =>
          selected ? (
            <FolderSearchFill size={SIDEBAR_MENU_ICONS_SIZE} />
          ) : (
            <FolderSearch size={SIDEBAR_MENU_ICONS_SIZE} />
          )
        }
        onClick={() => closeOutreachModalOnClick(() => navigateTo(routes.ratedCandidates))}
        tooltipStyle={classes.tooltip}
        tooltip={<Typography variant="supporting.helper">{dictionary.candidates}</Typography>}
      />
      <LargeIconButton
        selected={SidebarNavPages.Dashboard === activeRoute}
        icon={(selected) =>
          selected ? <PieChart_01Fill size={SIDEBAR_MENU_ICONS_SIZE} /> : <PieChart_01 size={SIDEBAR_MENU_ICONS_SIZE} />
        }
        onClick={() => closeOutreachModalOnClick(() => navigateTo(routes.dashboard))}
        tooltipStyle={classes.tooltip}
        tooltip={<Typography variant="supporting.helper">{dictionary.dashboard}</Typography>}
      />
      <LargeIconButton
        icon={() => <Settings_02 size={SIDEBAR_MENU_ICONS_SIZE} />}
        onClick={() => undefined}
        tooltip={<SidebarNavSettingsMenu />}
        tooltipStyle={classes.settingsIcon}
        tooltipArrowStyle={classes.fileSearchIconArrow}
        tooltipArrowStrokeStyle={theme.colors.neutral.variant[10]}
        tooltipArrowStrokeWidthStyle={1}
        tooltipArrow
        selected={false}
      />
      {isStaff && (
        <LargeIconButton
          selected={SidebarNavPages.Admin === activeRoute}
          icon={(selected) =>
            selected ? <LayoutLeftFill size={SIDEBAR_MENU_ICONS_SIZE} /> : <LayoutLeft size={SIDEBAR_MENU_ICONS_SIZE} />
          }
          onClick={() =>
            closeOutreachModalOnClick(() => {
              if (activeTokenType === 'impersonate') {
                disposeImpersonatedToken();
              }

              navigateTo(routes.admin.dashboard);
            })
          }
          tooltipStyle={classes.tooltip}
          tooltip={<Typography variant="supporting.helper">{dictionary.adminPanel}</Typography>}
        />
      )}
    </ButtonGroup>
  );
};

export default SidebarNav;
